export const en = {
  dir: 'ltr',
  label: 'English',
  nav: {
    home: 'Home', about: 'About Us', products: 'Products', materials: 'Materials',
    projects: 'Projects', insights: 'Insights', certifications: 'Certifications',
    careers: 'Careers', contact: 'Contact Us', requestQuote: 'Request Quote',
    materialsAndGrades: 'Materials & Grades', gradeFinder: 'Grade Finder', weightCalculator: 'Weight Calculator',
    megaCategories: 'Material Families', browseAll: 'Browse all', featured: 'Featured',
    megaSearch: 'Search grades, standards or equivalents…', megaSearchHint: 'Enter to open the first match',
    megaNoMatch: 'No grade matches', megaSeeAll: 'See all results', exStock: 'Ex-stock',
    viewDatasheet: 'View datasheet', mostAsked: 'Most asked',
    viewAllProducts: 'View All Products', search: 'Search the site',
  },
  answers: {
    kicker: 'Straight answers',
    title: 'Answers to the questions buyers actually ask',
    intro: 'Specification and buying questions answered from the standards and from what we hold in Dubai stock — no marketing, just the working detail you need before raising an enquiry.',
    metaTitle: 'Steel grade and certification answers',
    metaDesc: 'Direct answers on grade selection, certification and fabrication: 304 vs 316, SA387 vs A335, PREN for seawater, EN 10204 3.1, NACE MR0175 and more.',
    glossaryCta: 'Browse the glossary of terms',
    backToHub: 'All answers',
    relatedGrades: 'Grades referenced',
    ctaTitle: 'Need this confirmed for your job?',
    ctaBody: 'Send the duty, the size and the certification you need. We answer with a grade, a stock position and a price — not a brochure.',
  },
  glossary: {
    kicker: 'Defined terms',
    title: 'Steel, certification and fabrication glossary',
    intro: 'The terms that appear on mill certificates, datasheets and project specifications, defined in plain language with the numbers that matter.',
    metaTitle: 'Steel and certification glossary',
    metaDesc: 'Plain-language definitions of EN 10204 3.1, PREN, HBW, Charpy V-notch, PWHT, HIC, NACE MR0175, PSL2, TEMA, schedule, duplex and more.',
    searchPlaceholder: 'Search terms…',
    answersCta: 'See the answers hub',
  },
  editorial: {
    kicker: 'How this site is maintained',
    title: 'Editorial policy and automation disclosure',
    intro: 'Axicor is a trading desk, not a publisher. This site is maintained by an automated pipeline rather than an editorial team, and we would rather state exactly how it works than imply a newsroom that does not exist.',
    metaTitle: 'Editorial policy and automation disclosure',
    metaDesc: 'How Axicor produces its technical content: data sourced from standards and mill documentation, drafted and translated automatically, published under company responsibility.',
    contact: 'Found something wrong? Write to sales@axicor.ae with the page and the correction. Technical corrections are applied to the source data, so the fix propagates to every page, datasheet and catalogue PDF at once.',
    sections: [
      { title: 'Where the data comes from', body: 'Chemistry, mechanical and physical properties, standards and equivalents are taken from the governing standards (ASTM, ASME, EN, API, ISO) and from the mill documentation of the producers we buy from. Stock positions and lead times come from our own warehouse records.' },
      { title: 'How the pages are written', body: 'Descriptions, selection guidance and answers are drafted by an AI pipeline working from that structured data, then published under the responsibility of Axicor DWC LLC. The numbers are never invented by the model: they are rendered from the catalogue database, which is why a correction in one place updates every page.' },
      { title: 'Arabic content', body: 'The Arabic mirror of this site is machine-translated from the English source and reviewed for terminology consistency. Grade names, standard numbers and designations are deliberately left in their original form, because that is how they appear on certificates and purchase orders.' },
      { title: 'What this content is not', body: 'Nothing here is a design calculation, a code certification or a substitute for a qualified engineer. Grade selection guidance is written to narrow the field before you enquire. For a pressure boundary, a ballistic requirement or a sour service duty, the governing code and your own engineer decide.' },
      { title: 'Commercial relationship', body: 'Axicor sells the materials described on this site. We buy from the mills named on our mills page and hold no paid placements from any producer. Where a grade is not the right answer, the pages say so.' },
    ],
  },
  notFound: {
    title: 'That page is not here',
    body: 'The link may be out of date. Search for a grade or standard, or pick up one of the routes below.',
    search: 'Search grades and standards',
  },
  common: {
    loading: 'Loading…', readMore: 'Read more', viewAll: 'View all',
    requestQuote: 'Request a Quote', addToQuote: 'Add to quote list',
    downloadDatasheet: 'Download Datasheet', browseGrades: 'Browse grades',
    datasheetEn: 'English (PDF)', datasheetAr: 'Arabic (PDF)',
    backToMaterials: 'Back to materials', notFound: 'Not found',
    gradesCount: 'grades', formsAvailable: 'forms available',
    downloadCatalogue: 'Download catalogue', sortBy: 'Sort', filterBy: 'Filter',
  },
  catalogue: {
    kicker: 'One file, every grade',
    heading: 'The complete Axicor product catalogue',
    lead: 'Every grade we stock — chemistry, mechanical and physical properties, stocked forms and sizes, standards and international equivalents — generated live from our catalogue the moment you download it.',
    inside: "What's inside",
    insideLead: 'Cover, contents index, one datasheet per grade and a contact back page — ready to circulate internally.',
    statGrades: 'documented grades', statFamilies: 'material families',
    statPages: 'indexed datasheets', statRevision: 'Revision',
    emailedNote: 'We have also emailed a copy to {email}, so you can forward it to your team.',
    editions: 'Or take a focused edition',
    formTitle: 'Get the catalogue',
    formLead: 'Tell us where to send it and the PDF opens straight away. No spam, no newsletter sign-up.',
    name: 'Full name', email: 'Work email', company: 'Company', phone: 'Phone (optional)',
    country: 'Country (optional)',
    submit: 'Generate my catalogue', preparing: 'Building your PDF…',
    ready: 'Your catalogue is ready',
    readyText: 'The download has started. Use the buttons below if you need it again or want the Arabic edition.',
    downloadEn: 'Download English PDF', downloadAr: 'تحميل النسخة العربية',
    contents: 'Contents', why1: 'Always current',
    why1Text: 'Generated on the fly from our live catalogue, so the revision you download is never out of date.',
    why2: 'Certified data', why2Text: 'Every grade is supplied against EN 10204 3.1 mill certification with full heat traceability.',
    why3: 'Built to circulate', why3Text: 'One indexed PDF your engineering, procurement and QA teams can all work from.',
    errorEmail: 'Enter a valid work email so we can reach you.',
    failed: 'Could not build the catalogue. Please try again or email sales@axicor.ae',
    note: 'By requesting the catalogue you agree that our Dubai desk may contact you about your requirement.',
  },
  legal: { kicker: 'Legal' },
  industries: {
    oilgas: 'Oil & Gas', oilgasNote: 'Sour service, pipelines, pressure parts',
    petrochemical: 'Petrochemical', petrochemicalNote: 'Duplex and nickel alloy process lines',
    cement: 'Cement', cementNote: 'Chute, silo and clinker cooler wear liners',
    mining: 'Mining & Quarrying', miningNote: 'Buckets, crushers, screens, haul bodies',
    pharma: 'Pharmaceutical & Food', pharmaNote: 'Hygienic 316L plate, tube and fittings',
    desal: 'Power & Desalination', desalNote: 'Titanium and super duplex in seawater',
    marine: 'Marine & Offshore', marineNote: 'Topside spools, decks, splash zone',
    defence: 'Defence & Security', defenceNote: 'Protection plate to defined classes',
    fabrication: 'Construction & Fabrication', fabricationNote: 'Cut, profiled and kitted plate',
  },
  marketsPage: {
    kicker: 'Markets we supply',
    title: 'Stock in Dubai, plants across the Gulf and Africa.',
    lead: 'Fourteen markets served from one stock position. Each one has its own port, route, lead time and conformity regime — pick yours to see what we ship there and how fast it arrives.',
    seoTitle: 'Markets we supply — GCC and Africa steel supply',
    seoDesc: 'Axicor DWC LLC supplies certified steel, alloys and industrial products from Dubai stock into the UAE, Saudi Arabia, Oman, Qatar, Kuwait, Bahrain, Egypt, Morocco, Algeria, Nigeria, Kenya, South Africa, Tanzania and Ghana.',
    statCountries: 'markets served', statRegions: 'regions', statShipped: 'countries shipped to',
    openMarket: 'Open the market', openRegion: 'Open the region overview',
    ctaHeading: 'Tell us where the plant is and what it runs on.',
    ctaApplications: 'Browse by end use',
  },
  marketPage: {
    kicker: 'Markets',
    seoSuffix: 'steel and alloy supply from Dubai stock',
    notFound: 'That market page does not exist.',
    allMarkets: 'All markets',
    factLead: 'Typical lead time', factPort: 'Port of entry', factRoute: 'Route',
    factConformity: 'Certification & conformity', factLogistics: 'Logistics',
    countriesHeading: 'The {region} markets we supply.',
    sectorsKicker: 'Sectors served',
    sectorsHeading: 'What we ship into {country}, sector by sector.',
    openSector: 'Open the sector page',
    familiesHeading: 'The material families this market buys most.',
    siblingsHeading: 'Other markets in this region',
    ctaHeading: 'Shipping to {country}? Send the enquiry with the plant location.',
    ctaText: 'Give us the grade or the duty, the quantity and the delivery point. Our Dubai desk comes back with availability, the certification package and a landed lead time.',
  },
  marketIndustry: {
    title: '{sector} supply in {country}',
    seoTitle: '{sector} steel supplier in {country}',
    seoDesc: 'Grades, industrial products and lead times for {sector} plants in {country}, supplied from Axicor stock in Dubai with EN 10204 3.1 certification.',
    familiesHeading: 'Material families we lead with in {country}.',
    gradesHeading: 'Grades we supply for {sector} work in {country}.',
    otherSectors: 'Other sectors we supply in {country}',
    ctaHeading: 'Quote this against your {country} delivery point.',
    ctaIndustry: 'All {sector} material',
  },
  applicationsPage: {
    kicker: 'By end use',
    title: 'Pick the duty. We will put the material against it.',
    lead: 'Fifteen end uses we are asked about most, each one mapping the real duty — abrasion, seawater, sour service, high temperature — to the grades and products we would specify.',
    seoTitle: 'Applications — material selection by end use',
    seoDesc: 'Material selection guides by end use: cement chute liners, HPGR re-studding, crusher wear parts, seawater cooling lines, desalination tubing, pressure vessel plate, refinery heater coils, sour service pipelines and more.',
    chipGrades: 'grades listed',
    ctaHeading: 'Describe the duty and we will specify the material.',
    ctaMarkets: 'Markets we supply',
  },
  applicationPage: {
    seoSuffix: 'material selection and supply',
    notFound: 'That application page does not exist.',
    allApplications: 'All applications',
    notesKicker: 'Selection',
    notesHeading: 'How we choose the material for this duty.',
    gradesHeading: 'Grades we would specify.',
    productsHeading: 'Products that go with it.',
    faqHeading: 'Questions buyers ask about this duty.',
    relatedHeading: 'Related applications',
    ctaHeading: 'Send the duty, not just the grade.',
    ctaText: 'Tell us the service conditions, the current material and what life you are getting. Our Dubai desk comes back with the grade, the thickness and what is in stock.',
  },
  industriesPage: {
    kicker: 'Industries served',
    title: 'Steel chosen by the duty, not the datasheet.',
    lead: 'Nine sectors we supply into from Dubai. Pick yours to see the grades we hold, the industrial products that go with them and the material families to start from.',
    seoTitle: 'Industries we supply — steel and alloys by sector',
    seoDesc: 'Axicor DWC LLC supplies certified steel, alloys and industrial products into oil & gas, petrochemical, cement, mining, pharmaceutical, power & desalination, marine, defence and fabrication from stock in Dubai.',
    statSectors: 'sectors served', statGrades: 'grade listings', statProducts: 'product listings',
    gridKicker: 'Pick your sector',
    gridHeading: 'The plants and projects our steel ends up in.',
    chipGrades: 'grades', chipProducts: 'products',
    viewSector: 'Open the sector',
    ctaHeading: 'Not sure which sector page fits your duty?',
    ctaText: 'Send the application, the thickness and the tonnage. Our Dubai desk comes back with the grade or product we would put on it, what is in stock and what we can ship.',
  },
  industryPage: {
    kicker: 'By end use',
    statGrades: 'grades for this sector', statProducts: 'industrial products', statFamilies: 'material families',
    productsKicker: 'Industrial products',
    productsHeading: 'Industrial products we supply into {industry}.',
    viewProduct: 'View the product line',
    familiesHeading: 'Start with the family that fits the duty.',
    gradesKicker: 'Documented grades',
    gradesHeading: 'Grades we hold for {industry}.',
    gradesEmpty: 'Tell us the duty and our desk will put a grade against it.',
    loading: 'Loading this sector…',
    ctaHeading: 'Tell us the duty, not just the grade.',
    ctaText: 'Send the application, the thickness and the tonnage. Our Dubai desk comes back with the grade or product we would put on it, what is in stock and what we can ship.',
    ctaStock: 'Ex-stock Dubai on core items',
    ctaLead: 'Same working day response',
  },
  mills: {
    kicker: 'Principal mills',
    title: 'The mills behind what we sell.',
    lead: 'Axicor buys direct from the mills. Five of them we work with closely enough to call principal — the rest widen our reach when a grade, a size or a delivery window demands it.',
    seoTitle: 'Our mills — principal steel mill partners',
    seoDesc: 'The steel mills Axicor DWC LLC buys from direct, led by SSAB, Stancor Special Steel, Tsingshan, CITIC Pacific Special Steel and Hunan Valin Steel. Every heat supplied with EN 10204 3.1 certification.',
    statPrincipal: 'principal mills', statTotal: 'mills we source from', statCert: 'EN 10204 certification',
    homeHeading: 'Five mills we work with closely.',
    homeText: 'Direct mill relationships, not a broker chain — which is why we can confirm chemistry, certification and delivery before you commit.',
    homeNote: 'We source from 19 further mills when a grade or size calls for it.',
    seeAll: 'All our mills',
    principalHeading: 'Our principal mills.',
    principalText: 'The relationships we lead with. Material comes to Dubai direct from these mills, with the mill test certificate travelling with the heat.',
    networkHeading: 'The wider sourcing network.',
    networkText: 'Mills and brands we also buy from, so a grade we do not hold is rarely a grade we cannot get.',
    buyKicker: 'How we buy',
    buyHeading: 'Direct from the mill, certified to the heat.',
    buyText: 'Most traders cannot say the next four lines. We put them in writing because tender committees and third-party inspectors ask for exactly this.',
    buy1: 'We buy direct from the mill',
    buy1Text: 'Our orders go to the mill or its appointed channel — no broker chain in between. That is why we can confirm chemistry, certification and a delivery window before you commit, instead of after.',
    buy2: 'Mill test certificate with every heat',
    buy2Text: 'Material is released with its EN 10204 3.1 certificate. Chemistry and mechanicals trace back to the heat number stamped on the plate, so your QA has the document, not our assurance.',
    buy3: 'No re-stamped, no re-rolled plate',
    buy3Text: 'We never supply re-stamped material or plate of unverifiable origin. If a heat cannot be traced to the mill that made it, it does not leave our yard.',
    buy4: 'Third-party inspection welcome',
    buy4Text: 'SGS, TUV, BV, Lloyd\'s or your own inspector — call it at our yard or at the mill. We have nothing to hide and the paperwork is ready before you ask.',
    buyNote: 'Ask us for a sample mill test certificate before you place the order — we will send one.',
    millSite: 'Mill website',
    badgeGrade: 'Direct from {mill}, a principal mill',
    assure1: 'EN 10204 3.1 on every heat',
    assure1Text: 'Material arrives with its mill test certificate, so chemistry and mechanicals are traceable to the heat number, not to our word for it.',
    assure2: 'Genuine mill material only',
    assure2Text: 'We buy from the mill or its appointed channel. No re-stamped plate, no unverifiable origin, no surprises at third-party inspection.',
    assure3: 'Stock in Dubai, mill rolling when it pays',
    assure3Text: 'Core grades sit in Dubai for immediate release. For volume or a non-standard size we schedule a mill rolling and tell you the window up front.',
    ctaHeading: 'Need a grade from one of these mills?',
    rel: {
      distributor: 'Authorised distributor',
      approved_stockist: 'Approved stockist',
      principal: 'Principal mill partner',
      long_term: 'Long-term supply agreement',
      stock: 'We stock their material',
    },
  },
  footer: {
    newsletterKicker: 'Newsletter',
    newsletterHeading: 'Stock movements, new grades and technical notes — once a month.',
    newsletterPlaceholder: 'Your work email',
    subscribe: 'Subscribe',
    quickLinks: 'Navigate',
    materials: 'Materials',
    visitUs: 'Visit us',
    getDirections: 'Get directions',
    staffErp: 'Staff ERP', trademarks: 'Trademark notice',
  },
  products: {
    navLabel: 'Products',
    kicker: 'Eleven product lines, one desk',
    heading: 'Specialty Steels & Industrial Products',
    lead: 'Technical sourcing and supply solutions for demanding industrial applications — nine specialty steel and alloy families documented grade by grade, plus industrial valves, tungsten carbide wear studs and refractory materials supplied against your specification.',
    statProducts: 'product lines', statFamilies: 'material families', statGrades: 'documented grades',
    materialTag: 'Materials & grades', productTag: 'Industrial product',
    typesCount: 'types', types: 'Types & configurations', type: 'Type', spec: 'Specification',
    specifications: 'Specifications',
    requestLead: 'Send the specification, datasheet or line list and our Dubai desk will revert with options, documentation and pricing.',
    otherProducts: 'Other industrial products',
    backToProducts: 'Back to products',
    homeKicker: 'Beyond steel',
    homeHeading: 'Industrial products supplied to specification',
    homeLead: 'Valves, tungsten carbide wear studs and refractory materials — sourced and documented to the same standard as our steel.',
    viewAll: 'View all 11 product lines',
    ctaTitle: 'Send us the specification and we will handle the sourcing.',
    ctaText: 'Grade, form, class, drawing or line list — our Dubai desk replies within one business day.',
  },
  materials: {
    title: 'Materials & Grades',
    lead: 'Every material family we stock, with each grade documented by its chemical composition, mechanical properties and the forms we supply it in. Start with a family, or search the grade index directly.',
    openFinder: 'Open Grade Finder',
    browseByForm: 'Browse by product form',
    browseByFormLead: 'Know the form you need? Jump straight to every grade we stock in that form.',
    gradesInFamily: 'Grades in this family',
    suppliedAs: 'Supplied as',
    stocked: 'grades stocked',
    certified: 'Full mill certification',
    ctaTitle: 'Need a grade or size not listed?',
    ctaText: 'Our Dubai desk sources direct from the mills. Send your requirement and we will revert with availability.',
    noGrades: 'No grades published in this family yet.',
    catalogueCta: 'Prefer one file?',
    catalogueCtaText: 'Download every datasheet as a single indexed PDF.',
    filterForm: 'Product form', filterStandard: 'Standard', filterSearch: 'Search this family',
    sortDefault: 'Curated', sortAz: 'A → Z', sortZa: 'Z → A',
    showing: 'Showing', ofGrades: 'of', resetFilters: 'Reset',
  },
  grade: {
    forms: 'Available forms & sizes', form: 'Form', sizeRange: 'Size range',
    chemical: 'Chemical composition (%)', element: 'Element', min: 'Min', max: 'Max',
    mechanical: 'Mechanical properties', physical: 'Physical properties',
    property: 'Property', value: 'Value', unit: 'Unit',
    applications: 'Typical applications', standards: 'Standards',
    equivalents: 'Equivalent grades', industries: 'Industries served',
    enquire: 'Enquire', request: 'Request', authorised: 'authorised supply',
    faq: 'Frequently asked', stock: 'Stock status', leadTime: 'Typical lead time',
    gallery: 'Gallery', overview: 'Overview', specs: 'Specifications',
    requestLead: 'Send your thickness, size and quantity. Our desk replies with availability and pricing.',
    otherGrades: 'Other grades in',
  },
  finder: {
    resultsHeading: 'Every grade we document',
    title: 'Grade Finder',
    lead: 'Search the full grade index by designation, standard, equivalent or application.',
    placeholder: 'Try 316L, 1.4462, Hardox, Inconel, ASTM A240…',
    search: 'Search', filters: 'Filters', clearAll: 'Clear all filters',
    family: 'Material family', productForm: 'Product form', industry: 'Industry',
    searching: 'Searching…', found: 'found', noResults: 'No grades match this search.',
    reset: 'Reset filters',
  },
  insights: {
    title: 'Industry Insights',
    lead: 'Grade selection notes, fabrication guidance and market observations from our materials desk. Published continuously.',
    all: 'All', readArticle: 'Read the article', more: 'More insights',
    empty: 'The first articles are being prepared. Check back shortly.',
    ctaTitle: 'Sourcing the material discussed here?',
    ctaText: 'Send your specification and our Dubai desk will revert with availability.',
  },
  calc: {
    heading: 'Work out the theoretical weight',
    title: 'Steel Weight Calculator',
    lead: 'Work out theoretical weight before you enquire, then send the exact size straight to our desk.',
    shape: 'Shape', material: 'Material', pieces: 'Pieces',
    unitWeight: 'Weight per piece', totalWeight: 'Total weight', tonnes: 'tonnes',
    disclaimer: 'Theoretical weights based on nominal density. Mill tolerances mean delivered weight will vary.',
    shapes: { plate: 'Plate / sheet', round: 'Round bar', pipe: 'Pipe / tube', square: 'Square bar', flat: 'Flat bar' },
    fields: {
      thickness: 'Thickness (mm)', width: 'Width (mm)', length: 'Length (mm)',
      diameter: 'Diameter (mm)', od: 'Outside diameter (mm)', wall: 'Wall thickness (mm)', side: 'Side (mm)',
    },
  },
  rfq: {
    button: 'Quote list', heading: 'Your quote list', kicker: 'Request for quotation',
    empty: 'Your list is empty. Add grades from any material page to request them together.',
    size: 'Size / thickness', quantity: 'Quantity / tonnage',
    name: 'Your name', email: 'Work email', company: 'Company', phone: 'Phone',
    country: 'Delivery country / port',
    message: 'Project details, certification or delivery requirements',
    submit: 'Send quote request', note: 'You will receive an instant acknowledgement by email.',
    sentTitle: 'Request sent',
    sentText: 'A confirmation is on its way to your inbox and our Dubai desk has been alerted. Expect a reply with availability within one business day.',
    continue: 'Continue browsing', addFirst: 'Add at least one material first',
    failed: 'Could not send. Please try again or email sales@axicor.ae',
  },
  search: {
    placeholder: 'Search grades, standards, articles…',
    nothing: 'Nothing matches that search.',
    kinds: { grade: 'grade', article: 'article', page: 'page' },
    pages: {
      materials: 'Materials & Grades', grades: 'Grade Finder',
      calculator: 'Steel Weight Calculator', insights: 'Industry Insights',
      projects: 'Projects Delivered', certifications: 'Certifications',
      careers: 'Careers', contact: 'Contact & Quote Request',
      compare: 'Compare Grades',
    },
  },
  compare: {
    tool: 'Compare grades',
    title: 'Compare Grades',
    lead: 'Put up to four grades side by side, see exactly where they differ, and take the comparison away as a PDF.',
    add: 'Compare',
    added: 'Comparing',
    addGrade: 'Add a grade',
    searchGrades: 'Search grades…',
    noGrades: 'No more grades match.',
    tray: 'Comparing',
    clear: 'Clear',
    cta: 'Compare now',
    needTwo: 'Pick one more grade to compare',
    full: 'You can compare four grades at a time.',
    downloadPdf: 'Download comparison PDF',
    addAllToQuote: 'Add all to quote list',
    diffOnly: 'Differences only',
    differs: 'These values differ',
    property: 'Property',
    loading: 'Building the comparison…',
    loadFailed: 'Could not build that comparison.',
    verdictKicker: 'Which should you choose?',
    verdictLoading: 'Writing the engineering verdict…',
    verdictUnavailable: 'The verdict for this combination is still being written. Refresh in a moment.',
    emptyTitle: 'Pick two grades to get started',
    oneMoreTitle: 'Pick one more grade',
    oneMoreText: 'You have one grade selected. Add at least one more above and we will line up composition, strength, available forms and sizes so the differences are obvious.',    emptyText: 'Tap "Compare" on any grade card or datasheet page, or add grades above. We will line up composition, strength, available forms and sizes so the differences are obvious.',
    browseGrades: 'Browse all grades',
    fullDatasheet: 'datasheet',
    sideBySide: 'Side by side',
    faqTitle: 'Questions buyers ask',
    quoteBoth: 'Quote both grades',
    openInTool: 'Open in compare tool',
    pageMissing: 'That comparison is not published.',
    ctaTitle: 'Still not sure which one?',
    ctaText: 'Send us the application, the thickness and the tonnage. Our desk will come back with the grade we would put on the job and what we can ship from Dubai.',
    otherComparisons: 'Other comparisons in this family',
    popular: 'Popular comparisons',
    popularLead: 'The matchups buyers ask us about most, each with a full side-by-side breakdown.',
  },
  brands: {
    crumb: 'Brands compared',
    pageTitle: '{grade} brands compared',
    blockTitle: 'Equivalent brands we can also supply',
    seeAll: 'Compare all brands for this grade',
    sideBySide: 'Brand by brand',
    weLeadWith: 'We lead with this',
    authorised: 'We are authorised',
    strengths: 'Strengths',
    checks: 'Worth confirming',
    recommendation: 'What we would quote you',
    ourPosition: 'Our authorised distribution',
    missing: 'No brand comparison for that grade yet.',
    missingHelp: 'We have not published a brand-by-brand breakdown for this grade. Browse the catalogue for the grades we document, or tell us the application and we will advise on the equivalent we stock.',
    disclaimer: 'Brand names and trademarks belong to their respective mills and are used here only to identify equivalent products. Details are drawn from publicly published mill data; always confirm the certified specification and mill test certificate for your order.',
  },
  home: {
    since: 'Since', scroll: 'Scroll to explore',
    kicker: 'Specialty steel & high-performance alloy stockist — Dubai',
    heroFallback: 'Certified specialty steel and alloys, stocked in Dubai and shipped across the Gulf.',
    heroPanelCaption: 'Dubai stock — mill certified',
    heroSubFallback: 'Wear resistant plate, stainless, duplex, nickel alloys, high strength steel, titanium, armour plate and UHMW — mill certified and ready to cut.',
    statFounded: 'Trading since', statFamilies: 'Material families', statGrades: 'Documented grades', statCountries: 'Countries served',
    indexKicker: 'The stock', indexHeading: 'Every material family, every grade, documented to the certificate.',
    promiseKicker: 'The promise', promise1: 'Certified.', promise2: 'In stock.', promise3: 'Shipped.',
    promiseText: 'Every plate, pipe and bar leaves our Dubai facility with its mill test certificate. If it is on this site, we can tell you exactly what it is made of.',
    toolsKicker: 'Buying tools', toolsHeading: 'Specify it properly before you ever send an enquiry.',
    toolCompare: 'Compare grades', toolCompareText: 'Put up to four grades side by side — composition, strength, available forms — and take the comparison away as a PDF.',
    toolCalc: 'Weight calculator', toolCalcText: 'Plate, pipe, bar or sheet. Get the weight and the tonnage before you ask for a price.',
    toolFinder: 'Grade finder', toolFinderText: 'Filter by industry, standard or application.',
    toolSheets: 'Full datasheets', toolSheetsText: 'Composition, mechanical and physical properties, standards, international equivalents and stocked sizes — downloadable for every grade.',
    gradesKicker: 'From the floor', gradesHeading: 'What is moving this month',
    partnerDistributor: 'Authorized distributor',
    partnerDisclaimer: 'All trademarks, brand names and logos shown are the property of their respective owners. Axicor DWC LLC stocks and distributes material produced by these mills and claims no rights in their brands. Axicor is an authorized distributor of SSAB; the remaining names indicate material we hold and supply, not endorsement, sponsorship or authorisation by those producers.',
    trademarkNotice: 'Read the full trademark notice',
    clientsKicker: 'Trusted by',
    clientsDisclaimer: 'Customer names and logos are shown only with that customer\u2019s written permission. All other project references on this site are kept anonymous by agreement.',
    partnersKicker: 'Mills & brands',
    partnersHeading: 'We buy from the mills whose names are on the certificate.',
    partnersText: 'Authorized distributor for SSAB. Everything else comes from mills and master distributors whose documentation stands up to third-party inspection — because yours has to.',
    industriesKicker: 'Industries',
    industriesHeading: 'The plants and projects our steel ends up in.',
    insightsKicker: 'Insights',
    insightsArchive: 'Every technical note we have published.',
    ctaRail1Label: 'Phone', ctaRail1Value: '+971 4 357 9568',
    ctaRail2Label: 'Email', ctaRail2Value: 'sales@axicor.ae',
    ctaRail3Label: 'Response time', ctaRail3Value: 'Same working day',
    ctaHeading: 'Tell us the job, not just the grade.',
    ctaText: 'Send the application, the thickness and the tonnage. Our desk comes back with the grade we would put on it and what we can ship from Dubai.',
  },
  desk: {
    kicker: 'Axicor sales desk',
    title: 'Ask the technical desk',
    intro: 'Ask anything about the grades and industrial products we stock — chemistry, mechanicals, standards, equivalents, stocked forms. Answers come from our own datasheets.',
    starter1: 'Which wear plate suits a cement chute liner?',
    starter2: 'Hardox 450 vs Hardox 500 — what changes?',
    starter3: 'Do you stock 2507 super duplex seamless pipe?',
    placeholder: 'Type your question…',
    send: 'Send',
    close: 'Close',
    whatsapp: 'Continue on WhatsApp',
    handoff: 'Talk to a person',
    handoffTitle: 'Our Dubai desk will come back to you',
    handoffSubmit: 'Send to the desk',
    handoffDone: 'Sent. Our Dubai desk has your conversation and will reply within one business day.',
    name: 'Your name', email: 'Work email', company: 'Company (optional)',
    sending: 'Sending…',
    failed: 'Something went wrong. Please try again, or reach us on WhatsApp.',
    disclaimer: 'Answers come from our published datasheets. Prices, stock quantities and delivery dates are confirmed by the desk.',
  },
  about: {
    kicker: 'Who we are', stories: 'Our story',
    mission: 'Our Mission', vision: 'Our Vision', location: 'Our Location',
    partnersKicker: 'Our Partners', partnersTitle: 'Trusted mill partners',
    statFounded: 'Year Founded', statTeam: 'Team Members',
    statCountries: 'Countries Served', statGrades: 'Grades Stocked',
    pageTitle: 'Our Story',
    storyTitle: 'A decade of steel excellence',
    storyP1: 'Established in the year 2014, Axicor DWC LLC has rapidly grown over the years and has emerged as one of the major Manufacturer & Distributor of a comprehensive range of steel products required in construction, steel fabrication, shipbuilding, Oil & Gas industries etc. in the entire GCC region. Since its inception in 2014, the company has achieved and maintains a market leader status and today has attained unmatched level of growth in its operations.',
    storyP2: "Axicor is one of the Leading Supplier & Stockist of SSAB Wear Resistant Sheets & Plates having extensive knowledge on Wear Steels, Structural Steels and their properties. We follow total Quality Management System with the requisite Technical expertise to ensure that we meet our client's requirement and supply high quality products at competitive prices with timely delivery.",
    storyP3: 'Apart from the GCC region, our client base spans across the U.S.A., Canada, Europe, Germany, France, Italy, India, Korea, Japan and Australia. Our product portfolio spans stainless steel, duplex and super duplex, nickel alloys, wear-resistant plates, high-strength structural steel, titanium, armor steel, and ultra-high molecular weight polyethylene.',
    missionText: 'Our main mission is to become the leading global company in the steel industry and to provide our customers with quality and variety of Steel products at the right place at the right time, to surpass the customer needs.',
    visionText: 'To Be A Global Hub for Providing Complete Wear Solutions & to be known for its customer service, quality products, innovative technology and business ideas.',
    locationText: 'Unit 121, Building A5, PO Box 390667, DWC Head Quarters, Sheikh Mohammed Bin Zayed Road, Dubai, UAE - at the crossroads of global trade.',
  },
  projects: {
    kicker: 'Our Work', title: 'Project Gallery',
    lead: 'Showcasing our expertise in steel supply across mining, construction, oil & gas, and heavy industry.',
    heading: 'Material supplied into live projects',
    all: 'All', loading: 'Loading projects…',
    empty: 'No projects to display yet.', emptyHint: 'Check back soon for our latest work.',
    client: 'Client', noImage: 'No image',
  },
  certs: {
    kicker: 'Quality Assurance', title: 'Certifications',
    lead: 'Our commitment to quality is backed by internationally recognized certifications and standards.',
    heading: 'Approvals and certifications we hold',
    loading: 'Loading certifications…',
    empty: 'Certifications coming soon.', emptyHint: 'We are currently updating our certifications page.',
  },
  careers: {
    kicker: 'Join Our Team', title: 'Careers at Axicor',
    lead: "Build your career with a leading steel trading company. We're always looking for talented individuals.",
    statOpen: 'Open Positions', statDepartments: 'Departments',
    statCountries: 'Countries We Serve', statTeam: 'Team Members',
    heading: 'Open Positions', loading: 'Loading positions…',
    empty: 'No open positions at the moment.', emptyHint: 'Send your CV to',
    requirements: 'Requirements', benefits: 'Benefits', apply: 'Apply Now',
  },
  contact: {
    seoTitle: 'Contact the Axicor Desk',
    seoDesc: 'Talk to the Axicor DWC LLC trading desk in Dubai about grades, tonnage and delivery. Phone {phone}, email {email}.',
    kicker: 'Talk to the desk',
    title: 'Send the requirement. We come back with the metal.',
    lead: 'Tell us the grade, the thickness and where it has to land. A materials specialist reads every enquiry, checks it against stock and mill programmes, and replies with availability and next steps.',
    promise1: 'One business day', promise1Label: 'Reply on every enquiry',
    promise2: 'EN 10204 3.1 / 3.2', promise2Label: 'Certification with each lot',
    promise3: '5 principal mills', promise3Label: 'Direct mill relationships',
    ch_whatsapp: 'WhatsApp', ch_call: 'Call the desk', ch_email: 'Email sales', ch_desk: 'AI Sales Desk',
    whatsapp: 'WhatsApp', whatsappText: 'Fastest route for a drawing or a photo of the plate.',
    call: 'Call the desk', callText: 'Sunday to Thursday, Gulf business hours.',
    email: 'Email sales', emailText: 'Full specifications and tender documents.',
    desk: 'AI Sales Desk', deskValue: 'Ask now, 24/7',
    deskText: 'Answers from the grade datasheets in seconds.',
    formTitle: 'Start an enquiry',
    formDesc: 'Four fields are enough to get a conversation going. The rest helps us answer in one pass instead of three.',
    type_rfq: 'Quote / RFQ', type_technical: 'Technical question', type_logistics: 'Logistics',
    type_careers: 'Careers', type_general: 'General',
    group_grades: 'Materials & grades', group_products: 'Industrial products',
    name: 'Full name *', email: 'Work email *', phone: 'Phone / WhatsApp', company: 'Company',
    selectGrade: 'Grade or product of interest', quantity: 'Quantity (tonnes / pieces)',
    targetPort: 'Delivery port or city', subject: 'Subject',
    message: 'Sizes, standards, project and timeline *',
    attach: 'Attach drawings or specs', attaching: 'Uploading…',
    attachHint: 'PDF, image, DWG, DXF, STEP, Excel or ZIP. Up to 15MB each.',
    attachFailed: 'That file could not be attached.', remove: 'Remove',
    basketTitle: 'Include the {count} item(s) in your quote list',
    submit: 'Send enquiry', sending: 'Sending…',
    sent: 'Thank you for your inquiry. We will contact you shortly.',
    failed: 'Failed to submit. Please try again.',
    successTitle: 'Your enquiry is with the desk.',
    successText: 'A specialist has it now. Here is exactly what happens next.',
    step1: 'Received and logged', step1Text: 'You have a confirmation email with a copy of what you sent.',
    step2: 'Triaged and assigned',
    step2Text: 'We match the requirement to stock and mill programmes, and the right specialist picks it up.',
    step3: 'Offer with availability',
    step3Text: 'You get availability, certification scope and commercial terms — normally within one business day.',
    successCatalogue: 'Download the catalogue', successAnother: 'Send another enquiry',
    infoTitle: 'Dubai office', office: 'Corporate office',
    phoneLabel: 'Phone', emailLabel: 'Email', hours: 'Business hours',
    openNow: 'Open now', closedNow: 'Closed',
    gstNow: 'Local time in Dubai {time} (GST, UTC+4)',
    directions: 'Get directions',
    licence: 'Entity', licenceText: 'Axicor DWC LLC, licensed in Dubai World Central (DWC), United Arab Emirates.',
    deptTitle: 'Reach the right desk', deptText: 'Skip the queue and write to the team that owns your question.',
    dept_sales: 'Sales & quotations', dept_technical: 'Technical & grade selection',
    dept_logistics: 'Logistics & documents', dept_accounts: 'Accounts',
    quickTitle: 'Quick Response',
  },
};

export const ar = {
  dir: 'rtl',
  label: 'العربية',
  nav: {
    home: 'الرئيسية', about: 'من نحن', products: 'المنتجات', materials: 'المواد',
    projects: 'المشاريع', insights: 'المقالات التقنية', certifications: 'الشهادات',
    careers: 'الوظائف', contact: 'اتصل بنا', requestQuote: 'طلب عرض سعر',
    materialsAndGrades: 'المواد والدرجات', gradeFinder: 'محرك البحث عن الدرجات', weightCalculator: 'حاسبة الأوزان',
    megaCategories: 'عائلات المواد', browseAll: 'استعراض الكل', featured: 'مميّز',
    megaSearch: 'ابحث في الدرجات أو المواصفات أو المكافئات…', megaSearchHint: 'اضغط Enter لفتح أول نتيجة',
    megaNoMatch: 'لا توجد درجة مطابقة', megaSeeAll: 'عرض كل النتائج', exStock: 'متوفر بالمخزون',
    viewDatasheet: 'عرض ورقة البيانات', mostAsked: 'الأكثر طلباً',
    viewAllProducts: 'عرض جميع المنتجات', search: 'البحث في الموقع',
  },
  answers: {
    kicker: 'إجابات مباشرة',
    title: 'إجابات على الأسئلة التي يطرحها المشترون فعلياً',
    intro: 'أسئلة المواصفات والشراء مُجابة من المعايير ومن المخزون المتوفر لدينا في دبي — بلا تسويق، فقط التفاصيل العملية التي تحتاجها قبل إرسال طلبك.',
    metaTitle: 'إجابات حول درجات الفولاذ والشهادات',
    metaDesc: 'إجابات مباشرة حول اختيار الدرجات والشهادات والتصنيع: 304 مقابل 316، SA387 مقابل A335، قيمة PREN لمياه البحر، شهادة EN 10204 3.1، ومعيار NACE MR0175.',
    glossaryCta: 'استعرض قائمة المصطلحات',
    backToHub: 'كل الإجابات',
    relatedGrades: 'الدرجات المذكورة',
    ctaTitle: 'تحتاج تأكيداً لمشروعك؟',
    ctaBody: 'أرسل لنا ظروف التشغيل والمقاسات والشهادات المطلوبة. نرد بدرجة محددة وحالة المخزون والسعر — لا بكتالوج.',
  },
  glossary: {
    kicker: 'مصطلحات مُعرَّفة',
    title: 'قائمة مصطلحات الفولاذ والشهادات والتصنيع',
    intro: 'المصطلحات التي تظهر في شهادات المصانع وأوراق البيانات ومواصفات المشاريع، مُعرَّفة بلغة واضحة مع الأرقام التي تهم.',
    metaTitle: 'قائمة مصطلحات الفولاذ والشهادات',
    metaDesc: 'تعريفات واضحة لـ EN 10204 3.1 و PREN و HBW و اختبار شاربي و المعالجة الحرارية بعد اللحام و HIC و NACE MR0175 و PSL2 و TEMA والفولاذ الدوبلكس.',
    searchPlaceholder: 'ابحث في المصطلحات…',
    answersCta: 'انتقل إلى مركز الإجابات',
  },
  editorial: {
    kicker: 'كيف يُدار هذا الموقع',
    title: 'سياسة المحتوى والإفصاح عن الأتمتة',
    intro: 'أكسيكور شركة تجارية متخصصة في توريد المواد، وليست جهة نشر. يُدار هذا الموقع عبر منظومة مؤتمتة لا فريق تحرير، ونفضّل أن نوضح ذلك بدقة بدلاً من الإيحاء بوجود غرفة تحرير غير قائمة.',
    metaTitle: 'سياسة المحتوى والإفصاح عن الأتمتة',
    metaDesc: 'كيف تنتج أكسيكور محتواها التقني: بيانات مستقاة من المعايير ووثائق المصانع، تُصاغ وتُترجم آلياً وتُنشر على مسؤولية الشركة.',
    contact: 'وجدت خطأً؟ راسلنا على sales@axicor.ae مع رابط الصفحة والتصحيح. تُطبَّق التصحيحات التقنية على البيانات المصدرية، فينتقل التصحيح تلقائياً إلى كل صفحة وورقة بيانات وكتالوج.',
    sections: [
      { title: 'من أين تأتي البيانات', body: 'التركيب الكيميائي والخصائص الميكانيكية والفيزيائية والمعايير والمكافئات مأخوذة من المعايير الحاكمة (ASTM و ASME و EN و API و ISO) ومن وثائق المصانع التي نشتري منها. أما حالة المخزون ومدد التوريد فمن سجلات مستودعنا.' },
      { title: 'كيف تُكتب الصفحات', body: 'الأوصاف وإرشادات الاختيار والإجابات تُصاغ عبر منظومة ذكاء اصطناعي تعمل على تلك البيانات المهيكلة، ثم تُنشر على مسؤولية شركة أكسيكور. الأرقام لا يخترعها النموذج: تُستخرج من قاعدة بيانات الكتالوج، ولهذا يحدّث أي تصحيح واحد كل الصفحات.' },
      { title: 'المحتوى العربي', body: 'النسخة العربية من الموقع مترجمة آلياً من المصدر الإنجليزي ومراجعة لضبط اتساق المصطلحات. أسماء الدرجات وأرقام المعايير والتسميات تُترك بصيغتها الأصلية عن قصد، لأنها تظهر بهذا الشكل في الشهادات وأوامر الشراء.' },
      { title: 'ما لا يمثله هذا المحتوى', body: 'لا شيء هنا يُعدّ حساباً تصميمياً أو اعتماداً كودياً أو بديلاً عن مهندس مؤهل. إرشادات اختيار الدرجات مكتوبة لتضييق الخيارات قبل الاستفسار. أما حدود الضغط والمتطلبات الدفاعية وخدمة الغاز الحامضي فيحكمها الكود المعتمد ومهندسك.' },
      { title: 'العلاقة التجارية', body: 'تبيع أكسيكور المواد الموصوفة في هذا الموقع. نشتري من المصانع المذكورة في صفحة المصانع، ولا نتلقى أي مقابل مدفوع من أي منتج لترويجه. وحين لا تكون درجة ما هي الحل الصحيح، تقول الصفحات ذلك صراحة.' },
    ],
  },
  notFound: {
    title: 'هذه الصفحة غير موجودة',
    body: 'قد يكون الرابط قديماً. ابحث عن درجة أو مواصفة، أو تابع من أحد المسارات أدناه.',
    search: 'ابحث في الدرجات والمواصفات',
  },
  common: {
    loading: 'جارٍ التحميل…', readMore: 'اقرأ المزيد', viewAll: 'عرض الكل',
    requestQuote: 'طلب عرض سعر', addToQuote: 'أضف إلى قائمة التسعير',
    downloadDatasheet: 'تحميل البيانات الفنية', browseGrades: 'استعرض الدرجات',
    datasheetEn: 'إنجليزي (PDF)', datasheetAr: 'عربي (PDF)',
    backToMaterials: 'العودة إلى المواد', notFound: 'غير موجود',
    gradesCount: 'درجة', formsAvailable: 'شكل متوفر',
    downloadCatalogue: 'تحميل الكتالوج', sortBy: 'ترتيب', filterBy: 'تصفية',
  },
  catalogue: {
    kicker: 'ملف واحد يضم كل الدرجات',
    heading: 'كتالوج أكسيكور الكامل للمنتجات',
    lead: 'كل درجة نوفرها — التركيب الكيميائي والخصائص الميكانيكية والفيزيائية والأشكال والمقاسات المتوفرة والمواصفات والدرجات المكافئة — يُنشأ مباشرة من كتالوجنا لحظة التحميل.',
    inside: 'ماذا يحتوي',
    insideLead: 'غلاف، وفهرس محتويات، وصفحة بيانات لكل درجة، وصفحة اتصال خلفية — جاهز للتعميم داخلياً.',
    statGrades: 'درجة موثّقة', statFamilies: 'عائلة مواد',
    statPages: 'صفحة بيانات مفهرسة', statRevision: 'المراجعة',
    emailedNote: 'أرسلنا نسخة أيضاً إلى {email} حتى تتمكن من تمريرها إلى فريقك.',
    editions: 'أو اختر نسخة متخصصة',
    formTitle: 'احصل على الكتالوج',
    formLead: 'أخبرنا إلى أين نرسله وسيبدأ تحميل الملف فوراً. بدون رسائل مزعجة أو اشتراك بريدي.',
    name: 'الاسم الكامل', email: 'البريد الإلكتروني للعمل', company: 'الشركة', phone: 'الهاتف (اختياري)',
    country: 'البلد (اختياري)',
    submit: 'أنشئ الكتالوج الخاص بي', preparing: 'جارٍ إنشاء ملف PDF…',
    ready: 'كتالوجك جاهز',
    readyText: 'بدأ التحميل. استخدم الأزرار أدناه إذا أردت الملف مرة أخرى أو النسخة الإنجليزية.',
    downloadEn: 'Download English PDF', downloadAr: 'تحميل النسخة العربية',
    contents: 'المحتويات', why1: 'محدّث دائماً',
    why1Text: 'يُنشأ مباشرة من كتالوجنا الحالي، لذا فإن النسخة التي تحملها ليست قديمة أبداً.',
    why2: 'بيانات موثّقة', why2Text: 'كل درجة تُورَّد بشهادة مصنع EN 10204 3.1 مع تتبع كامل لرقم الصهر.',
    why3: 'جاهز للتعميم', why3Text: 'ملف PDF واحد مفهرس يمكن لفرق الهندسة والمشتريات والجودة العمل عليه.',
    errorEmail: 'أدخل بريداً إلكترونياً صحيحاً لنتمكن من التواصل معك.',
    failed: 'تعذر إنشاء الكتالوج. حاول مرة أخرى أو راسلنا على sales@axicor.ae',
    note: 'بطلب الكتالوج فإنك توافق على أن يتواصل معك مكتبنا في دبي بخصوص احتياجك.',
  },
  legal: { kicker: 'إشعارات قانونية' },
  industries: {
    oilgas: 'النفط والغاز', oilgasNote: 'الخدمة الحامضة وخطوط الأنابيب وأجزاء الضغط',
    petrochemical: 'البتروكيماويات', petrochemicalNote: 'خطوط معالجة من الدوبلكس وسبائك النيكل',
    cement: 'الأسمنت', cementNote: 'مبطنات المزالق والصوامع ومبردات الكلنكر',
    mining: 'التعدين والمحاجر', miningNote: 'القواديس والكسارات والمناخل وصناديق النقل',
    pharma: 'الأدوية والأغذية', pharmaNote: 'ألواح وأنابيب 316L الصحية',
    desal: 'الطاقة وتحلية المياه', desalNote: 'التيتانيوم والسوبر دوبلكس في مياه البحر',
    marine: 'البحرية والمنشآت البحرية', marineNote: 'الأنابيب السطحية والأسطح ومنطقة الرشّ',
    defence: 'الدفاع والأمن', defenceNote: 'ألواح حماية بفئات محددة',
    fabrication: 'البناء والتصنيع', fabricationNote: 'ألواح مقطوعة ومجهزة وجاهزة للتركيب',
  },
  marketsPage: {
    kicker: 'الأسواق التي نخدمها',
    title: 'مخزون في دبي، ومصانع في الخليج وأفريقيا.',
    lead: 'أربعة عشر سوقاً نخدمها من مخزون واحد. لكل سوق ميناؤه وطريقه ومدة توريده ونظام مطابقته — اختر سوقك لتعرف ما نوردّه إليه وبأي سرعة يصل.',
    seoTitle: 'الأسواق التي نخدمها — توريد الفولاذ للخليج وأفريقيا',
    seoDesc: 'توّرد Axicor DWC LLC الفولاذ والسبائك والمنتجات الصناعية المعتمدة من مخزونها في دبي إلى الإمارات والسعودية وعُمان وقطر والكويت والبحرين ومصر والمغرب والجزائر ونيجيريا وكينيا وجنوب أفريقيا وتنزانيا وغانا.',
    statCountries: 'سوقاً نخدمه', statRegions: 'منطقة', statShipped: 'دولة نشحن إليها',
    openMarket: 'استعرض السوق', openRegion: 'استعرض نظرة المنطقة',
    ctaHeading: 'أخبرنا بموقع المصنع وبما يعمل عليه.',
    ctaApplications: 'تصفّح حسب الاستخدام النهائي',
  },
  marketPage: {
    kicker: 'الأسواق',
    seoSuffix: 'توريد الفولاذ والسبائك من مخزون دبي',
    notFound: 'صفحة السوق هذه غير موجودة.',
    allMarkets: 'كل الأسواق',
    factLead: 'مدة التوريد المعتادة', factPort: 'ميناء الدخول', factRoute: 'المسار',
    factConformity: 'الشهادات والمطابقة', factLogistics: 'الخدمات اللوجستية',
    countriesHeading: 'أسواق {region} التي نخدمها.',
    sectorsKicker: 'القطاعات التي نخدمها',
    sectorsHeading: 'ما نوردّه إلى {country}، قطاعاً بقطاع.',
    openSector: 'استعرض صفحة القطاع',
    familiesHeading: 'عائلات المواد الأكثر طلباً في هذا السوق.',
    siblingsHeading: 'أسواق أخرى في المنطقة',
    ctaHeading: 'تشحن إلى {country}؟ أرسل الطلب مع موقع المصنع.',
    ctaText: 'أعطنا الدرجة أو ظروف التشغيل والكمية ونقطة التسليم، وسيعود مكتبنا في دبي بالتوفر وحزمة الشهادات ومدة التوريد حتى الوصول.',
  },
  marketIndustry: {
    title: 'توريد {sector} في {country}',
    seoTitle: 'مورّد فولاذ {sector} في {country}',
    seoDesc: 'الدرجات والمنتجات الصناعية ومدد التوريد لمصانع {sector} في {country}، من مخزون أكسيكور في دبي بشهادة EN 10204 3.1.',
    familiesHeading: 'عائلات المواد التي نتقدّم بها في {country}.',
    gradesHeading: 'الدرجات التي نوردّها لأعمال {sector} في {country}.',
    otherSectors: 'قطاعات أخرى نخدمها في {country}',
    ctaHeading: 'اطلب عرض سعر حتى نقطة التسليم في {country}.',
    ctaIndustry: 'كل مواد {sector}',
  },
  applicationsPage: {
    kicker: 'حسب الاستخدام النهائي',
    title: 'اختر ظروف التشغيل، ونحن نحدد المادة المناسبة.',
    lead: 'خمسة عشر استخداماً نهائياً نُسأل عنها أكثر من غيرها، يربط كل منها ظروف التشغيل الحقيقية — التآكل، مياه البحر، الخدمة الحامضة، الحرارة العالية — بالدرجات والمنتجات التي نوصي بها.',
    seoTitle: 'التطبيقات — اختيار المواد حسب الاستخدام النهائي',
    seoDesc: 'أدلة اختيار المواد حسب الاستخدام: مبطنات مزالق الأسمنت، إعادة تزويد أسطوانات HPGR، أجزاء تآكل الكسارات، خطوط تبريد مياه البحر، أنابيب التحلية، ألواح أوعية الضغط، ملفات أفران التكرير، وخطوط الأنابيب للخدمة الحامضة.',
    chipGrades: 'درجة مدرجة',
    ctaHeading: 'اشرح ظروف التشغيل وسنحدد المادة.',
    ctaMarkets: 'الأسواق التي نخدمها',
  },
  applicationPage: {
    seoSuffix: 'اختيار المواد والتوريد',
    notFound: 'صفحة التطبيق هذه غير موجودة.',
    allApplications: 'كل التطبيقات',
    notesKicker: 'الاختيار',
    notesHeading: 'كيف نختار المادة لهذه الظروف.',
    gradesHeading: 'الدرجات التي نوصي بها.',
    productsHeading: 'المنتجات المرتبطة بها.',
    faqHeading: 'أسئلة يطرحها المشترون حول هذه الظروف.',
    relatedHeading: 'تطبيقات ذات صلة',
    ctaHeading: 'أرسل ظروف التشغيل، لا الدرجة فقط.',
    ctaText: 'أخبرنا بظروف الخدمة والمادة الحالية والعمر الذي تحصل عليه، وسيعود مكتبنا في دبي بالدرجة والسماكة وما هو متوفر في المخزون.',
  },
  industriesPage: {
    kicker: 'القطاعات التي نخدمها',
    title: 'الفولاذ يُختار حسب ظروف التشغيل، لا حسب ورقة البيانات.',
    lead: 'تسعة قطاعات نوردها من دبي. اختر قطاعك لتستعرض الدرجات المتوفرة لدينا والمنتجات الصناعية المرتبطة بها وعائلات المواد التي تبدأ منها.',
    seoTitle: 'القطاعات التي نخدمها — الفولاذ والسبائك حسب القطاع',
    seoDesc: 'توّرد Axicor DWC LLC الفولاذ والسبائك والمنتجات الصناعية المعتمدة لقطاعات النفط والغاز والبتروكيماويات والأسمنت والتعدين والأدوية والطاقة وتحلية المياه والبحرية والدفاع والتصنيع من مخزونها في دبي.',
    statSectors: 'قطاعاً نخدمه', statGrades: 'إدراج درجات', statProducts: 'إدراج منتجات',
    gridKicker: 'اختر قطاعك',
    gridHeading: 'المصانع والمشاريع التي ينتهي بها فولاذنا.',
    chipGrades: 'درجة', chipProducts: 'منتج',
    viewSector: 'استعرض القطاع',
    ctaHeading: 'غير متأكد أي قطاع يناسب تطبيقك؟',
    ctaText: 'أرسل التطبيق والسماكة والكمية، وسيعود مكتبنا في دبي بالدرجة أو المنتج المناسب، وما هو متوفر في المخزون، وما يمكن شحنه.',
  },
  industryPage: {
    kicker: 'حسب الاستخدام النهائي',
    statGrades: 'درجة لهذا القطاع', statProducts: 'منتج صناعي', statFamilies: 'عائلة مواد',
    productsKicker: 'المنتجات الصناعية',
    productsHeading: 'المنتجات الصناعية التي نوردها لقطاع {industry}.',
    viewProduct: 'استعرض خط المنتج',
    familiesHeading: 'ابدأ من العائلة التي تناسب ظروف التشغيل.',
    gradesKicker: 'الدرجات الموثقة',
    gradesHeading: 'الدرجات التي نوفرها لقطاع {industry}.',
    gradesEmpty: 'أخبرنا بظروف التشغيل وسيحدد مكتبنا الدرجة المناسبة.',
    loading: 'جارٍ تحميل القطاع…',
    ctaHeading: 'حدّثنا عن ظروف التشغيل، لا عن الدرجة فقط.',
    ctaText: 'أرسل التطبيق والسماكة والكمية، وسيعود مكتبنا في دبي بالدرجة أو المنتج المناسب، وما هو متوفر في المخزون، وما يمكن شحنه.',
    ctaStock: 'مخزون جاهز في دبي للأصناف الأساسية',
    ctaLead: 'ردّ في نفس يوم العمل',
  },
  mills: {
    kicker: 'المصانع الرئيسية',
    title: 'المصانع التي تقف خلف ما نبيعه.',
    lead: 'تشتري أكسيكور من المصانع مباشرة. خمسة منها نعمل معها بشكل وثيق نسميها مصانعنا الرئيسية، والبقية توسّع نطاقنا عندما تتطلب الدرجة أو المقاس أو موعد التسليم ذلك.',
    seoTitle: 'مصانعنا — شركاء التوريد الرئيسيون',
    seoDesc: 'المصانع التي تشتري منها Axicor DWC LLC مباشرة، وفي مقدمتها SSAB و Stancor Special Steel و Tsingshan و CITIC Pacific Special Steel و Hunan Valin. كل صبّة تُورَّد بشهادة EN 10204 3.1.',
    statPrincipal: 'مصنع رئيسي', statTotal: 'مصنعاً نشتري منه', statCert: 'شهادة EN 10204',
    homeHeading: 'خمسة مصانع نعمل معها بشكل وثيق.',
    homeText: 'علاقات مباشرة مع المصانع، لا سلسلة وسطاء — ولذلك نؤكد لك التركيب الكيميائي والشهادات وموعد التسليم قبل أن تلتزم.',
    homeNote: 'كما نشتري من 19 مصنعاً آخر عندما تتطلب الدرجة أو المقاس ذلك.',
    seeAll: 'كل مصانعنا',
    principalHeading: 'مصانعنا الرئيسية.',
    principalText: 'العلاقات التي نتقدّم بها. تصل المواد إلى دبي من هذه المصانع مباشرة، وتُرفق شهادة اختبار المصنع مع كل صبّة.',
    networkHeading: 'شبكة التوريد الأوسع.',
    networkText: 'مصانع وعلامات نشتري منها أيضاً، ولهذا فإن الدرجة التي لا نخزّنها نادراً ما تكون درجة لا نستطيع توفيرها.',
    buyKicker: 'كيف نشتري',
    buyHeading: 'مباشرة من المصنع، وبشهادة لكل صبّة.',
    buyText: 'معظم التجار لا يستطيعون قول السطور الأربعة التالية. نكتبها بوضوح لأن لجان المناقصات وجهات الفحص المستقلة تطلبها تحديداً.',
    buy1: 'نشتري مباشرة من المصنع',
    buy1Text: 'أوامر الشراء تذهب إلى المصنع أو قناته المعتمدة — بلا سلسلة وسطاء. ولهذا نؤكد التركيب الكيميائي والشهادات وموعد التسليم قبل أن تلتزم، لا بعده.',
    buy2: 'شهادة اختبار المصنع مع كل صبّة',
    buy2Text: 'تُسلَّم المواد مع شهادة EN 10204 3.1، ويعود التركيب الكيميائي والخصائص الميكانيكية إلى رقم الصبّة المختوم على اللوح، فيكون لدى قسم الجودة لديكم المستند نفسه لا مجرد تأكيدنا.',
    buy3: 'لا ألواح معاد ختمها أو إعادة درفلتها',
    buy3Text: 'لا نورّد مواد معاد ختمها ولا ألواحاً مجهولة المصدر. وإذا لم يكن بالإمكان تتبّع الصبّة إلى المصنع الذي أنتجها فإنها لا تخرج من مستودعنا.',
    buy4: 'الفحص من جهة ثالثة مرحّب به',
    buy4Text: 'SGS أو TUV أو BV أو Lloyd\'s أو مفتشكم الخاص — في مستودعنا أو في المصنع. لا شيء نخفيه، والمستندات جاهزة قبل أن تطلبوها.',
    buyNote: 'اطلب منا نموذج شهادة اختبار مصنع قبل إصدار أمر الشراء وسنرسله لك.',
    millSite: 'موقع المصنع',
    badgeGrade: 'مباشرة من {mill}، أحد مصانعنا الرئيسية',
    assure1: 'شهادة EN 10204 3.1 لكل صبّة',
    assure1Text: 'تصل المواد مع شهادة اختبار المصنع، فيكون التركيب الكيميائي والخصائص الميكانيكية قابلاً للتتبع حتى رقم الصبّة.',
    assure2: 'مواد مصنع أصلية فقط',
    assure2Text: 'نشتري من المصنع أو قناته المعتمدة. لا ألواح معاد ختمها، ولا مصادر غير موثقة، ولا مفاجآت عند الفحص من جهة ثالثة.',
    assure3: 'مخزون في دبي، ودرفلة من المصنع عند الحاجة',
    assure3Text: 'الدرجات الأساسية متوفرة في دبي للتسليم الفوري. وللكميات الكبيرة أو المقاسات غير القياسية نحدد موعد درفلة في المصنع ونبلغك بالمدة مقدماً.',
    ctaHeading: 'تحتاج درجة من أحد هذه المصانع؟',
    rel: {
      distributor: 'موزّع معتمد',
      approved_stockist: 'مخزِّن معتمد',
      principal: 'شريك مصنع رئيسي',
      long_term: 'اتفاقية توريد طويلة الأجل',
      stock: 'نخزّن موادهم',
    },
  },
  footer: {
    newsletterKicker: 'النشرة البريدية',
    newsletterHeading: 'حركة المخزون والدرجات الجديدة والملاحظات الفنية — مرة كل شهر.',
    newsletterPlaceholder: 'بريد العمل الإلكتروني',
    subscribe: 'اشترك',
    quickLinks: 'التنقل',
    materials: 'المواد',
    visitUs: 'زورونا',
    getDirections: 'احصل على الاتجاهات',
    staffErp: 'نظام ERP للعاملين', trademarks: 'إشعار العلامات التجارية',
  },
  products: {
    navLabel: 'المنتجات',
    kicker: 'أحد عشر خط منتجات من مكتب واحد',
    heading: 'الفولاذ المتخصص والمنتجات الصناعية',
    lead: 'حلول التوريد التقني للتطبيقات الصناعية الصعبة — تسع عائلات من الفولاذ والسبائك الخاصة موثّقة درجة بدرجة، إضافة إلى الصمامات الصناعية ومسامير كربيد التنجستن المقاومة للتآكل والمواد الحرارية، تُورَّد حسب مواصفاتكم.',
    statProducts: 'خط منتجات', statFamilies: 'عائلة مواد', statGrades: 'درجة موثّقة',
    materialTag: 'المواد والدرجات', productTag: 'منتج صناعي',
    typesCount: 'نوع', types: 'الأنواع والتكوينات', type: 'النوع', spec: 'المواصفة',
    specifications: 'المواصفات',
    requestLead: 'أرسل المواصفة أو ورقة البيانات أو قائمة الخطوط وسيوافيك مكتبنا في دبي بالخيارات والوثائق والسعر.',
    otherProducts: 'منتجات صناعية أخرى',
    backToProducts: 'العودة إلى المنتجات',
    homeKicker: 'ما بعد الفولاذ',
    homeHeading: 'منتجات صناعية تُورَّد حسب المواصفات',
    homeLead: 'الصمامات ومسامير كربيد التنجستن والمواد الحرارية — نوردها ونوثّقها بنفس معيار الفولاذ لدينا.',
    viewAll: 'استعرض خطوط المنتجات الـ11',
    ctaTitle: 'أرسل لنا المواصفة وسنتولى التوريد.',
    ctaText: 'الدرجة أو الشكل أو الفئة أو الرسم أو قائمة الخطوط — يرد مكتبنا في دبي خلال يوم عمل واحد.',
  },
  materials: {
    title: 'المواد والدرجات',
    lead: 'كل عائلات المواد التي نوفّرها، وكل درجة موثّقة بتركيبها الكيميائي وخصائصها الميكانيكية والأشكال التي نوردها بها. ابدأ من عائلة المادة، أو ابحث في فهرس الدرجات مباشرة.',
    openFinder: 'ابحث عن درجة',
    browseByForm: 'استعرض حسب شكل المنتج',
    browseByFormLead: 'تعرف الشكل الذي تحتاجه؟ انتقل مباشرة إلى كل الدرجات المتوفرة بهذا الشكل.',
    gradesInFamily: 'الدرجات في هذه العائلة',
    suppliedAs: 'تُورَّد بالأشكال',
    stocked: 'درجة في المخزون',
    certified: 'شهادات مصنع كاملة',
    ctaTitle: 'تحتاج درجة أو مقاساً غير مدرج؟',
    ctaText: 'مكتبنا في دبي يورّد مباشرة من المصانع. أرسل متطلباتك وسنوافيك بالتوافر.',
    noGrades: 'لا توجد درجات منشورة في هذه العائلة بعد.',
    catalogueCta: 'تفضل ملفاً واحداً؟',
    catalogueCtaText: 'حمّل كل صفحات البيانات في ملف PDF واحد مفهرس.',
    filterForm: 'شكل المنتج', filterStandard: 'المواصفة', filterSearch: 'ابحث في هذه العائلة',
    sortDefault: 'الترتيب المقترح', sortAz: 'أ → ي', sortZa: 'ي → أ',
    showing: 'يتم عرض', ofGrades: 'من', resetFilters: 'إعادة تعيين',
  },
  grade: {
    forms: 'الأشكال والمقاسات المتوفرة', form: 'الشكل', sizeRange: 'نطاق المقاسات',
    chemical: 'التركيب الكيميائي (%)', element: 'العنصر', min: 'الحد الأدنى', max: 'الحد الأقصى',
    mechanical: 'الخصائص الميكانيكية', physical: 'الخصائص الفيزيائية',
    property: 'الخاصية', value: 'القيمة', unit: 'الوحدة',
    applications: 'التطبيقات النموذجية', standards: 'المواصفات القياسية',
    equivalents: 'الدرجات المكافئة', industries: 'القطاعات التي نخدمها',
    enquire: 'استفسار', request: 'اطلب', authorised: 'توريد معتمد',
    faq: 'أسئلة متكررة', stock: 'حالة المخزون', leadTime: 'مدة التوريد',
    gallery: 'معرض الصور', overview: 'نظرة عامة', specs: 'المواصفات',
    requestLead: 'أرسل السماكة والمقاس والكمية، وسيوافيك مكتبنا بالتوافر والسعر.',
    otherGrades: 'درجات أخرى في',
  },
  finder: {
    resultsHeading: 'كل درجة موثّقة لدينا',
    title: 'محرك البحث عن الدرجات',
    lead: 'ابحث في فهرس الدرجات الكامل بالتسمية أو المواصفة أو الدرجة المكافئة أو التطبيق.',
    placeholder: 'جرّب 316L أو 1.4462 أو Hardox أو Inconel أو ASTM A240…',
    search: 'بحث', filters: 'عوامل التصفية', clearAll: 'إزالة كل عوامل التصفية',
    family: 'عائلة المادة', productForm: 'شكل المنتج', industry: 'القطاع',
    searching: 'جارٍ البحث…', found: 'نتيجة', noResults: 'لا توجد درجات مطابقة لهذا البحث.',
    reset: 'إعادة التصفية',
  },
  insights: {
    title: 'المقالات التقنية',
    lead: 'ملاحظات في اختيار الدرجات، وإرشادات التصنيع، وقراءات في السوق من مكتب المواد لدينا. تُنشر بشكل مستمر.',
    all: 'الكل', readArticle: 'اقرأ المقال', more: 'مقالات أخرى',
    empty: 'يجري إعداد المقالات الأولى. تفضل بالعودة قريباً.',
    ctaTitle: 'هل تبحث عن المادة المذكورة في هذا المقال؟',
    ctaText: 'أرسل مواصفاتك وسيوافيك مكتبنا في دبي بالتوافر.',
  },
  calc: {
    heading: 'احسب الوزن النظري',
    title: 'حاسبة وزن الفولاذ',
    lead: 'احسب الوزن النظري قبل الاستفسار، ثم أرسل المقاس المطلوب إلى مكتبنا مباشرة.',
    shape: 'الشكل', material: 'المادة', pieces: 'عدد القطع',
    unitWeight: 'الوزن لكل قطعة', totalWeight: 'الوزن الإجمالي', tonnes: 'طن',
    disclaimer: 'الأوزان نظرية ومحسوبة على الكثافة الاسمية. تفاوتات المصنع تعني اختلاف الوزن الفعلي.',
    shapes: { plate: 'ألواح / صفائح', round: 'قضبان دائرية', pipe: 'أنابيب', square: 'قضبان مربعة', flat: 'قضبان مسطحة' },
    fields: {
      thickness: 'السماكة (مم)', width: 'العرض (مم)', length: 'الطول (مم)',
      diameter: 'القطر (مم)', od: 'القطر الخارجي (مم)', wall: 'سماكة الجدار (مم)', side: 'طول الضلع (مم)',
    },
  },
  rfq: {
    button: 'قائمة التسعير', heading: 'قائمة التسعير الخاصة بك', kicker: 'طلب عرض سعر',
    empty: 'قائمتك فارغة. أضف الدرجات من أي صفحة مادة لطلبها معاً.',
    size: 'المقاس / السماكة', quantity: 'الكمية / الوزن',
    name: 'الاسم', email: 'البريد الإلكتروني', company: 'الشركة', phone: 'الهاتف',
    country: 'بلد أو ميناء التسليم',
    message: 'تفاصيل المشروع أو الشهادات أو متطلبات التسليم',
    submit: 'إرسال طلب التسعير', note: 'ستصلك رسالة تأكيد فورية على بريدك الإلكتروني.',
    sentTitle: 'تم إرسال الطلب',
    sentText: 'رسالة التأكيد في طريقها إلى بريدك، وقد تم تنبيه مكتبنا في دبي. توقع الرد بالتوافر خلال يوم عمل واحد.',
    continue: 'متابعة التصفح', addFirst: 'أضف مادة واحدة على الأقل',
    failed: 'لم يتم الإرسال. حاول مرة أخرى أو راسلنا على sales@axicor.ae',
  },
  search: {
    placeholder: 'ابحث في الدرجات والمواصفات والمقالات…',
    nothing: 'لا توجد نتائج مطابقة.',
    kinds: { grade: 'درجة', article: 'مقال', page: 'صفحة' },
    pages: {
      materials: 'المواد والدرجات', grades: 'محرك البحث عن الدرجات',
      calculator: 'حاسبة وزن الفولاذ', insights: 'المقالات التقنية',
      projects: 'المشاريع المنفذة', certifications: 'الشهادات',
      careers: 'الوظائف', contact: 'اتصل بنا وطلب التسعير',
      compare: 'مقارنة الدرجات',
    },
  },
  compare: {
    tool: 'مقارنة الدرجات',
    title: 'مقارنة الدرجات',
    lead: 'ضع حتى أربع درجات جنباً إلى جنب، واطّلع على الفروق بدقة، ثم حمّل المقارنة بصيغة PDF.',
    add: 'مقارنة',
    added: 'قيد المقارنة',
    addGrade: 'أضف درجة',
    searchGrades: 'ابحث في الدرجات…',
    noGrades: 'لا توجد درجات أخرى مطابقة.',
    tray: 'قيد المقارنة',
    clear: 'إزالة الكل',
    cta: 'قارن الآن',
    needTwo: 'اختر درجة إضافية لبدء المقارنة',
    full: 'يمكنك مقارنة أربع درجات في المرة الواحدة.',
    downloadPdf: 'تحميل المقارنة PDF',
    addAllToQuote: 'أضف الكل إلى قائمة التسعير',
    diffOnly: 'الفروق فقط',
    differs: 'هذه القيم مختلفة',
    property: 'الخاصية',
    loading: 'جارٍ إعداد المقارنة…',
    loadFailed: 'لم نتمكن من إعداد هذه المقارنة.',
    verdictKicker: 'أي درجة تختار؟',
    verdictLoading: 'جارٍ إعداد التوصية الهندسية…',
    verdictUnavailable: 'التوصية لهذه المجموعة قيد الإعداد. حدّث الصفحة بعد لحظات.',
    emptyTitle: 'اختر درجتين للبدء',
    oneMoreTitle: 'اختر درجة إضافية واحدة',
    oneMoreText: 'لديك درجة واحدة محددة. أضف درجة أخرى على الأقل من الأعلى وسنصف التركيب الكيميائي والمقاومة والأشكال والمقاسات المتاحة لتظهر الفروق بوضوح.',
    emptyText: 'اضغط «مقارنة» على أي بطاقة درجة أو صفحة ورقة بيانات، أو أضف الدرجات من الأعلى. سنصف التركيب الكيميائي والمقاومة والأشكال والمقاسات المتاحة لتظهر الفروق بوضوح.',
    browseGrades: 'استعرض جميع الدرجات',
    fullDatasheet: 'ورقة البيانات',
    sideBySide: 'جنباً إلى جنب',
    faqTitle: 'أسئلة يطرحها المشترون',
    quoteBoth: 'اطلب تسعير الدرجتين',
    openInTool: 'افتح في أداة المقارنة',
    pageMissing: 'هذه المقارنة غير منشورة.',
    ctaTitle: 'ما زلت غير متأكد أي درجة تناسبك؟',
    ctaText: 'أرسل لنا التطبيق والسماكة والكمية، وسيعود فريقنا بالدرجة التي نوصي بها لهذا العمل وما يمكن شحنه من دبي.',
    otherComparisons: 'مقارنات أخرى في هذه العائلة',
    popular: 'المقارنات الأكثر طلباً',
    popularLead: 'المقارنات التي يسألنا عنها المشترون أكثر من غيرها، مع تحليل كامل جنباً إلى جنب.',
  },
  brands: {
    crumb: 'مقارنة العلامات التجارية',
    pageTitle: 'مقارنة العلامات التجارية لـ {grade}',
    blockTitle: 'علامات تجارية مكافئة يمكننا توريدها أيضاً',
    seeAll: 'قارن جميع العلامات التجارية لهذه الدرجة',
    sideBySide: 'علامة تجارية مقابل أخرى',
    weLeadWith: 'نوصي بها',
    authorised: 'موزع معتمد',
    strengths: 'نقاط القوة',
    checks: 'يجدر التأكد منه',
    recommendation: 'ما سنقدمه لك في التسعير',
    ourPosition: 'التوزيع المعتمد لدينا',
    missing: 'لا توجد مقارنة علامات تجارية لهذه الدرجة بعد.',
    missingHelp: 'لم ننشر بعد مقارنة بين العلامات التجارية لهذه الدرجة. تصفح الكتالوج أو أخبرنا بالتطبيق وسنرشدك إلى الدرجة المكافئة المتوفرة لدينا.',
    disclaimer: 'أسماء العلامات التجارية والعلامات المسجلة ملك لمصانعها، وتُستخدم هنا فقط للإشارة إلى المنتجات المكافئة. التفاصيل مستمدة من بيانات المصانع المنشورة؛ يُرجى دائماً تأكيد المواصفة المعتمدة وشهادة اختبار المصنع لطلبك.',
  },
  home: {
    since: 'منذ', scroll: 'انزل للاستكشاف',
    kicker: 'موزّع الفولاذ المتخصص والسبائك عالية الأداء — دبي',
    heroFallback: 'فولاذ وسبائك متخصصة معتمدة، متوفرة في دبي وتُشحن إلى كل دول الخليج.',
    heroPanelCaption: 'مخزون دبي — بشهادات المصنع',
    heroSubFallback: 'ألواح مقاومة للتآكل، فولاذ مقاوم للصدأ، دوبلكس، سبائك النيكل، فولاذ عالي المقاومة، تيتانيوم، ألواح تدريع و UHMW — بشهادات مصنع وجاهزة للقطع.',
    statFounded: 'نعمل منذ', statFamilies: 'عائلات المواد', statGrades: 'درجات موثّقة', statCountries: 'دولة نخدمها',
    indexKicker: 'المخزون', indexHeading: 'كل عائلة مواد، وكل درجة، موثّقة حتى الشهادة.',
    promiseKicker: 'التزامنا', promise1: 'معتمد.', promise2: 'متوفر.', promise3: 'مشحون.',
    promiseText: 'كل لوح وأنبوب وقضيب يخرج من منشأتنا في دبي مع شهادة اختبار المصنع. إن كان معروضاً على هذا الموقع، فنحن نعرف تركيبه بالتفصيل.',
    toolsKicker: 'أدوات الشراء', toolsHeading: 'حدّد مواصفاتك بدقة قبل أن ترسل طلب التسعير.',
    toolCompare: 'مقارنة الدرجات', toolCompareText: 'ضع حتى أربع درجات جنباً إلى جنب — التركيب والمقاومة والأشكال المتاحة — وحمّل المقارنة بصيغة PDF.',
    toolCalc: 'حاسبة الأوزان', toolCalcText: 'ألواح أو أنابيب أو قضبان أو صفائح. احسب الوزن والكمية قبل أن تطلب السعر.',
    toolFinder: 'محرك البحث عن الدرجات', toolFinderText: 'فلترة حسب القطاع أو المواصفة أو التطبيق.',
    toolSheets: 'أوراق بيانات كاملة', toolSheetsText: 'التركيب والخصائص الميكانيكية والفيزيائية والمواصفات والمكافئات الدولية والمقاسات المتوفرة — قابلة للتحميل لكل درجة.',
    gradesKicker: 'من أرض المستودع', gradesHeading: 'الأكثر حركة هذا الشهر',
    partnerDistributor: 'موزع معتمد',
    partnerDisclaimer: 'جميع العلامات التجارية والأسماء والشعارات المعروضة هي ملك لأصحابها. تقوم Axicor DWC LLC بتخزين وتوزيع مواد من إنتاج هذه المصانع ولا تدّعي أي حقوق في علاماتها التجارية. Axicor موزع معتمد لشركة SSAB؛ أما الأسماء الأخرى فتشير إلى المواد التي نوفرها ونوردها، وليست دليلاً على أي رعاية أو تفويض من تلك الجهات.',
    trademarkNotice: 'اقرأ إشعار العلامات التجارية كاملاً',
    clientsKicker: 'عملاؤنا',
    clientsDisclaimer: 'لا تُعرض أسماء العملاء وشعاراتهم إلا بموافقة كتابية منهم. وتبقى جميع المراجع الأخرى للمشاريع على هذا الموقع مجهولة الهوية بالاتفاق.',
    partnersKicker: 'المصانع والعلامات',
    partnersHeading: 'نشتري من المصانع التي تظهر أسماؤها على الشهادة.',
    partnersText: 'موزع معتمد لشركة SSAB. وكل ما عدا ذلك يأتي من مصانع وموزعين رئيسيين توثيقهم يصمد أمام التفتيش الخارجي — لأن توثيقكم يجب أن يصمد كذلك.',
    industriesKicker: 'القطاعات',
    industriesHeading: 'المصانع والمشاريع التي ينتهي بها فولاذنا.',
    insightsKicker: 'مقالات تقنية',
    insightsArchive: 'كل ملاحظة تقنية نشرناها.',
    ctaRail1Label: 'الهاتف', ctaRail1Value: '+971 4 357 9568',
    ctaRail2Label: 'البريد الإلكتروني', ctaRail2Value: 'sales@axicor.ae',
    ctaRail3Label: 'زمن الرد', ctaRail3Value: 'في نفس يوم العمل',
    ctaHeading: 'حدّثنا عن المشروع، لا عن الدرجة فقط.',
    ctaText: 'أرسل التطبيق والسماكة والكمية، وسيعود فريقنا بالدرجة التي نوصي بها وما يمكن شحنه من دبي.',
  },
  desk: {
    kicker: 'مكتب أكسيكور الفني',
    title: 'اسأل المكتب الفني',
    intro: 'اسأل عن أي درجة أو منتج صناعي نوفره — التركيب الكيميائي والخصائص الميكانيكية والمواصفات والمكافئات والأشكال المتوفرة. الإجابات مستمدة من أوراق بياناتنا.',
    starter1: 'أي لوح مقاوم للتآكل يناسب مبطنات مزالق الأسمنت؟',
    starter2: 'ما الفرق بين Hardox 450 و Hardox 500؟',
    starter3: 'هل يتوفر أنبوب سوبر دوبلكس 2507 بدون لحام؟',
    placeholder: 'اكتب سؤالك…',
    send: 'إرسال',
    close: 'إغلاق',
    whatsapp: 'المتابعة على واتساب',
    handoff: 'التحدث إلى أحد موظفينا',
    handoffTitle: 'سيتواصل معك مكتبنا في دبي',
    handoffSubmit: 'أرسل إلى المكتب',
    handoffDone: 'تم الإرسال. وصلت محادثتك إلى مكتبنا في دبي وسيردّ خلال يوم عمل واحد.',
    name: 'الاسم', email: 'البريد الإلكتروني للعمل', company: 'الشركة (اختياري)',
    sending: 'جارٍ الإرسال…',
    failed: 'حدث خطأ. حاول مرة أخرى أو تواصل معنا على واتساب.',
    disclaimer: 'الإجابات مستمدة من أوراق بياناتنا المنشورة. أما الأسعار والكميات ومواعيد التسليم فيؤكدها المكتب.',
  },
  about: {
    kicker: 'من نحن', stories: 'قصتنا',
    mission: 'مهمتنا', vision: 'رؤيتنا', location: 'موقعنا',
    partnersKicker: 'شركاؤنا', partnersTitle: 'مصانع نثق بها',
    statFounded: 'سنة التأسيس', statTeam: 'عدد الفريق',
    statCountries: 'دولة نخدمها', statGrades: 'درجة في المخزون',
    pageTitle: 'قصتنا',
    storyTitle: 'عقد من التميّز في الفولاذ',
    storyP1: 'تأسست Axicor DWC LLC عام 2014 ونمت سريعاً لتصبح من أبرز مورّدي وموزّعي مجموعة شاملة من منتجات الفولاذ المطلوبة في البناء وتصنيع المعادن وبناء السفن وقطاع النفط والغاز في منطقة الخليج بأكملها. ومنذ انطلاقها حافظت الشركة على موقعها الرائد في السوق وحققت نمواً لا يُضاهى في عملياتها.',
    storyP2: 'أكسيكور من أبرز موردي ومخزني ألواح وصفائح SSAB المقاومة للتآكل، بخبرة واسعة في الفولاذ المقاوم للتآكل والفولاذ الإنشائي وخصائصهما. نعمل بنظام إدارة جودة متكامل وبالخبرة الفنية اللازمة لتلبية متطلبات عملائنا وتوريد منتجات عالية الجودة بأسعار تنافسية وفي الوقت المحدد.',
    storyP3: 'إلى جانب منطقة الخليج، يمتد عملاؤنا إلى الولايات المتحدة وكندا وأوروبا وألمانيا وفرنسا وإيطاليا والهند وكوريا واليابان وأستراليا. وتشمل محفظة منتجاتنا الفولاذ المقاوم للصدأ والدوبلكس والسوبر دوبلكس وسبائك النيكل والألواح المقاومة للتآكل والفولاذ الإنشائي عالي المقاومة والتيتانيوم وفولاذ التدريع والبولي إيثيلين فائق الوزن الجزيئي.',
    missionText: 'مهمتنا الأساسية أن نصبح الشركة الرائدة عالمياً في صناعة الفولاذ، وأن نوفّر لعملائنا جودة وتنوعاً في منتجات الفولاذ في المكان والوقت المناسبين، بما يتجاوز احتياجاتهم.',
    visionText: 'أن نكون مركزاً عالمياً لحلول مقاومة التآكل المتكاملة، وأن نُعرف بخدمة العملاء وجودة المنتجات والتقنيات وأفكار العمل المبتكرة.',
    locationText: 'وحدة 121، مبنى A5، ص.ب 390667، المقر الرئيسي لدبي الجنوب، شارع الشيخ محمد بن زايد، دبي، الإمارات — في ملتقى طرق التجارة العالمية.',
  },
  projects: {
    kicker: 'أعمالنا', title: 'معرض المشاريع',
    lead: 'خبرتنا في توريد الفولاذ لقطاعات التعدين والبناء والنفط والغاز والصناعات الثقيلة.',
    heading: 'مواد وُرِّدت لمشاريع قائمة',
    all: 'الكل', loading: 'جارٍ تحميل المشاريع…',
    empty: 'لا توجد مشاريع للعرض بعد.', emptyHint: 'عد إلينا قريباً لمتابعة أحدث أعمالنا.',
    client: 'العميل', noImage: 'لا توجد صورة',
  },
  certs: {
    kicker: 'ضمان الجودة', title: 'الشهادات',
    lead: 'التزامنا بالجودة مدعوم بشهادات ومواصفات معترف بها دولياً.',
    heading: 'الاعتمادات والشهادات التي نحملها',
    loading: 'جارٍ تحميل الشهادات…',
    empty: 'الشهادات قادمة قريباً.', emptyHint: 'نعمل حالياً على تحديث صفحة الشهادات.',
  },
  careers: {
    kicker: 'انضم إلى فريقنا', title: 'الوظائف في أكسيكور',
    lead: 'ابنِ مسارك المهني مع شركة رائدة في تجارة الفولاذ. نبحث دائماً عن الكفاءات.',
    statOpen: 'وظائف متاحة', statDepartments: 'الأقسام',
    statCountries: 'دولة نخدمها', statTeam: 'عدد الفريق',
    heading: 'الوظائف المتاحة', loading: 'جارٍ تحميل الوظائف…',
    empty: 'لا توجد وظائف متاحة حالياً.', emptyHint: 'أرسل سيرتك الذاتية إلى',
    requirements: 'المتطلبات', benefits: 'المزايا', apply: 'تقدّم الآن',
  },
  contact: {
    seoTitle: 'تواصل مع مكتب أكسيكور',
    seoDesc: 'تحدث إلى مكتب التداول في دبي حول الدرجات والكميات والتسليم. هاتف {phone} — بريد {email}.',
    kicker: 'تحدث إلى المكتب',
    title: 'أرسل المتطلب، ونعود إليك بالمعدن.',
    lead: 'أخبرنا بالدرجة والسماكة وميناء التسليم. يقرأ كل استفسار مختص مواد، ويطابقه مع المخزون وبرامج المصانع، ثم يعود إليك بالتوافر والخطوات التالية.',
    promise1: 'يوم عمل واحد', promise1Label: 'رد على كل استفسار',
    promise2: 'EN 10204 3.1 / 3.2', promise2Label: 'شهادة مع كل دفعة',
    promise3: '5 مصانع رئيسية', promise3Label: 'علاقات مباشرة مع المصانع',
    ch_whatsapp: 'واتساب', ch_call: 'اتصل بالمكتب', ch_email: 'راسل المبيعات', ch_desk: 'مكتب المبيعات الذكي',
    whatsapp: 'واتساب', whatsappText: 'الأسرع لإرسال رسم هندسي أو صورة للصفيحة.',
    call: 'اتصل بالمكتب', callText: 'من الأحد إلى الخميس، ساعات العمل الخليجية.',
    email: 'راسل المبيعات', emailText: 'للمواصفات الكاملة ومستندات المناقصات.',
    desk: 'مكتب المبيعات الذكي', deskValue: 'اسأل الآن، على مدار الساعة',
    deskText: 'إجابات من بيانات الدرجات في ثوانٍ.',
    formTitle: 'ابدأ استفساراً',
    formDesc: 'أربعة حقول تكفي لبدء الحوار، وبقية الحقول تساعدنا على الإجابة من المرة الأولى.',
    type_rfq: 'طلب عرض سعر', type_technical: 'سؤال فني', type_logistics: 'الشحن والخدمات اللوجستية',
    type_careers: 'الوظائف', type_general: 'عام',
    group_grades: 'المواد والدرجات', group_products: 'المنتجات الصناعية',
    name: 'الاسم الكامل *', email: 'بريد العمل *', phone: 'الهاتف / واتساب', company: 'الشركة',
    selectGrade: 'الدرجة أو المنتج المطلوب', quantity: 'الكمية (طن / قطعة)',
    targetPort: 'ميناء أو مدينة التسليم', subject: 'الموضوع',
    message: 'المقاسات والمعايير والمشروع والجدول الزمني *',
    attach: 'أرفق الرسومات أو المواصفات', attaching: 'جارٍ الرفع…',
    attachHint: 'PDF أو صورة أو DWG أو DXF أو STEP أو Excel أو ZIP، حتى 15 ميجابايت للملف.',
    attachFailed: 'تعذر إرفاق هذا الملف.', remove: 'إزالة',
    basketTitle: 'أضف العناصر ({count}) من قائمة طلب السعر',
    submit: 'إرسال الاستفسار', sending: 'جارٍ الإرسال…',
    sent: 'شكراً لاستفسارك. سنتواصل معك قريباً.',
    failed: 'تعذر الإرسال. حاول مرة أخرى.',
    successTitle: 'استفسارك الآن عند المكتب.',
    successText: 'يتابعه مختص الآن. وهذه الخطوات التالية بالتفصيل.',
    step1: 'تم الاستلام والتسجيل', step1Text: 'وصلتك رسالة تأكيد تتضمن نسخة من بيانات استفسارك.',
    step2: 'الفرز والإحالة',
    step2Text: 'نطابق المتطلب مع المخزون وبرامج المصانع، ويتولّاه المختص المناسب.',
    step3: 'عرض مع التوافر',
    step3Text: 'تحصل على التوافر ونطاق الشهادات والشروط التجارية، عادةً خلال يوم عمل واحد.',
    successCatalogue: 'تحميل الكتالوج', successAnother: 'إرسال استفسار آخر',
    infoTitle: 'مكتب دبي', office: 'المكتب الرئيسي',
    phoneLabel: 'الهاتف', emailLabel: 'البريد الإلكتروني', hours: 'ساعات العمل',
    openNow: 'مفتوح الآن', closedNow: 'مغلق',
    gstNow: 'التوقيت المحلي في دبي {time} (UTC+4)',
    directions: 'احصل على الاتجاهات',
    licence: 'الكيان', licenceText: 'أكسيكور دي دبليو سي ذ.م.م، مرخّصة في دبي ورلد سنترال، الإمارات العربية المتحدة.',
    deptTitle: 'اختر المكتب المناسب', deptText: 'راسل الفريق المسؤول عن سؤالك مباشرة.',
    dept_sales: 'المبيعات وعروض الأسعار', dept_technical: 'الشؤون الفنية واختيار الدرجات',
    dept_logistics: 'الشحن والمستندات', dept_accounts: 'الحسابات',
    quickTitle: 'ردّ سريع',
  },
};

export const dictionaries = { en, ar };
